import { SHOW_VP_CORRECTION } from "@/config/displayPolicy";

/**
 * KROVEX 증기압(`predicted_vp`) 표시 규칙 — 단일 출처.
 *
 * 2026-08-28 신설. 그 전에는 보정 상수 `VP_CORRECTION = 2.0` 이 세 파일
 * (FeaturesTable · FeatureDetailPanel · FeatureDetailInlineRow)에 각각
 * 복제돼 있었고, 그 탓에 같은 화면이 같은 물질을 서로 다르게 말했다:
 *
 * - 검출 물질 표의 칩은 **숫자는 원값**인데 **색과 툴팁은 보정값**이었다.
 * - 데스크톱 상세 패널은 보정값을 "(보정)"이라 밝히고,
 *   모바일 인라인 행은 같은 보정값을 아무 표기 없이 원값처럼 보여줬다.
 *
 * 이 모듈이 숫자·색·툴팁·라벨을 모두 같은 입력에서 만들어 어긋날 수 없게 한다.
 * 보정을 화면에 반영할지는 `SHOW_VP_CORRECTION` 하나가 정한다(근거는 그
 * 플래그의 주석). 값을 지우지는 않는다 — 백엔드가 준 원값은 그대로 쓴다.
 */

/**
 * 옛 화면이 `predicted_vp` 에 더하던 보정량(log Pa).
 *
 * **출처 불명.** 도입 커밋(`3c39bcb`)의 주석 "체계적으로 약 2 log Pa 낮게
 * 예측" 외에 근거가 없고, 실측상 이 값은 모델 편향이 아니라 학습 타깃의
 * 단위 문제(log10 mmHg → log10 Pa 오프셋 2.125)로 보인다. 그마저도 학습
 * 데이터에 스케일이 다른 두 블록이 섞여 있어 단일 상수로는 교정되지 않는다.
 * 지우지 않고 남겨 둔 것은 인하대 확인 후 되살릴 수 있게 하기 위해서다.
 * 자세한 근거: `src/config/displayPolicy.ts` 의 `SHOW_VP_CORRECTION`.
 */
export const VP_CORRECTION_LOG_PA = 2.0;

/**
 * 휘발성 구간 경계(보정 스케일, log Pa).
 *
 * 우리가 정한 값이다 — 백엔드(`ai_mock_server._enhance_*`)는 같은 값에
 * 원값 기준 3.0 / 1.0 을 쓴다. 보정 표시가 보류된 동안에는 이 구간도 내지
 * 않는다(보정 스케일 위에서만 성립하는 경계이므로).
 */
const HIGH_VOLATILITY_LOG_PA = 3.5;
const MID_VOLATILITY_LOG_PA = 2.0;

/** 중립 칩 — 구간 해석을 내지 않을 때 쓴다. */
const NEUTRAL_CHIP_CLASS = "bg-gray-100 text-gray-700";

/**
 * 화면에 찍을 증기압 값.
 *
 * 보정 표시가 보류돼 있으면 모델 원값 그대로다(= DB·API·보고서와 같은 값).
 */
export function displayVp(predictedVp: number): number {
  return SHOW_VP_CORRECTION ? predictedVp + VP_CORRECTION_LOG_PA : predictedVp;
}

/**
 * 휘발성 구간 라벨. 보정 표시가 보류된 동안에는 `null` — 경계가 보정 스케일
 * 위에서만 성립하기 때문이다. 호출부는 null 이면 라벨을 그리지 않는다.
 */
export function vpVolatilityLabel(predictedVp: number): string | null {
  if (!SHOW_VP_CORRECTION) return null;
  const corrected = predictedVp + VP_CORRECTION_LOG_PA;
  if (corrected > HIGH_VOLATILITY_LOG_PA) return "고휘발";
  if (corrected > MID_VOLATILITY_LOG_PA) return "중간";
  return "저휘발";
}

/**
 * 증기압 칩의 배경색. 구간 라벨을 낼 수 없는 동안에는 중립색만 쓴다 —
 * 라벨을 감추고 색으로 같은 해석을 주면 감춘 의미가 없다(JSD 라벨과 같은
 * 처리: `FeaturesTable.getJsdColor`).
 */
export function vpChipClass(predictedVp: number): string {
  const label = vpVolatilityLabel(predictedVp);
  if (label === null) return NEUTRAL_CHIP_CLASS;
  if (label === "고휘발") return "bg-red-100 text-red-700";
  if (label === "중간") return "bg-yellow-100 text-yellow-700";
  return "bg-green-100 text-green-700";
}

/**
 * 증기압 툴팁 — **화면에 찍힌 숫자와 같은 값**을 말한다.
 * 옛 화면은 숫자는 원값, 툴팁은 보정값이라 한 칩이 두 값을 말했다.
 */
export function vpTooltip(predictedVp: number): string {
  const shown = displayVp(predictedVp).toFixed(1);
  if (!SHOW_VP_CORRECTION) {
    return `증기압 ${shown} log Pa (KROVEX 모델 원값). 단위·보정 기준이 확인되기 전이라 휘발성 구간은 표시하지 않습니다.`;
  }
  return `증기압(보정 +${VP_CORRECTION_LOG_PA.toFixed(1)}): ~${shown} log Pa — ${vpVolatilityLabel(predictedVp)}`;
}

/** 상세 패널·인라인 행의 증기압 항목 이름. 보정이 반영될 때만 그렇게 밝힌다. */
export function vpFieldLabel(): string {
  return SHOW_VP_CORRECTION ? "증기압 (보정)" : "증기압 (모델 원값)";
}

/** 상세 패널·인라인 행에 찍을 증기압 문자열. */
export function vpValueText(predictedVp: number): string {
  const shown = displayVp(predictedVp).toFixed(1);
  return SHOW_VP_CORRECTION ? `~${shown} log Pa` : `${shown} log Pa`;
}
